import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { CaptchaGate, type CaptchaHandle } from '../../components/security/CaptchaGate'
import { isCaptchaSatisfied } from '../../utils/captcha'
import { useAuthStore } from '../../stores/authStore'
import { Eye, EyeOff, CheckCircle, Users } from 'lucide-react'
import logo from '../../assets/logo/logo.png'
import { getInvitationByToken } from '../../utils/emailInvites'
import type { Invitation } from '../../utils/emailInvites'
import logger from '../../utils/logger'
import { showPreferences } from '../../utils/cookieConsentConfig'
import { track } from '../../utils/analytics'

export default function Register() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const captchaRef = useRef<CaptchaHandle>(null)

  // Zaproszenie
  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const [invitationLoading, setInvitationLoading] = useState(false)

  const { signUp } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Sprawdź czy użytkownik przychodzi z zaproszenia
  useEffect(() => {
    const redirect = searchParams.get('redirect')
    if (redirect && redirect.includes('/invitation/')) {
      const token = redirect.split('/invitation/')[1]
      if (token) {
        setInvitationLoading(true)
        getInvitationByToken(token)
          .then((invite) => {
            if (invite) {
              setInvitation(invite)
              setEmail(invite.email) // Pre-wypełnij email z zaproszenia
            }
          })
          .catch((err) => {
            logger.error('❌ Błąd pobierania zaproszenia:', err)
          })
          .finally(() => {
            setInvitationLoading(false)
          })
      }
    }
  }, [searchParams])

  // Password validation
  const passwordValidation = {
    length: password.length >= 8,
    hasLetter: /[a-zA-Z]/.test(password),
    hasNumber: /\d/.test(password),
    match: password === confirmPassword && password.length > 0
  }

  const isPasswordValid = Object.values(passwordValidation).every(Boolean)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isPasswordValid) {
      setError('Sprawdź wymagania hasła')
      return
    }

    if (!acceptedTerms) {
      setError('Aby utworzyć konto, zaakceptuj Regulamin i zapoznaj się z Polityką prywatności')
      return
    }

    if (!isCaptchaSatisfied(captchaToken)) {
      setError('Poczekaj na zakończenie weryfikacji antybotowej')
      return
    }

    // Usunięto: klik-into-submit track – mierzymy klik w onClick przycisku

    setLoading(true)
    setError('')

    try {
      await signUp(email, password, captchaToken)
      setSuccess(true)
      track('sign_up', { method: 'email', invited: !!invitation })

      // Utwórz preferencje użytkownika w bazie danych
      try {
        // Poczekaj na inicjalizację authStore, aby mieć dostęp do user.id
        const { user } = useAuthStore.getState()

        if (user) {
          logger.debug('🔧 Tworzenie preferencji użytkownika...')

          // Import apiService i supabase na żądanie
          const { supabase } = await import('../../lib/supabase')

          // Utwórz preferencje użytkownika
          const { error } = await supabase
            .from('user_preferences')
            .insert({
              user_id: user.id,
              color_scheme: 'default',
              notifications_email: true,
              notifications_calendar: true,
              notifications_members: true,
              updated_at: new Date().toISOString()
            })

          if (error) {
            logger.error('❌ Błąd tworzenia preferencji użytkownika:', error)
          } else {
            logger.debug('✅ Preferencje użytkownika utworzone')
          }
        } else {
          logger.debug('⚠️ Brak ID użytkownika, nie można utworzyć preferencji')
        }
      } catch (prefsError) {
        logger.error('❌ Błąd tworzenia preferencji użytkownika:', prefsError)
      }

      // Zapisz metadane akceptacji regulaminu i polityki prywatności
      try {
        const { user } = useAuthStore.getState()
        if (user) {
          const now = new Date().toISOString()
          const { error: metaError } = await (await import('../../lib/supabase')).supabase.auth.updateUser({
            data: {
              tosAcceptedAt: now,
              tosVersion: '1.0',
              privacyAcceptedAt: now,
              privacyVersion: '1.0'
            }
          })
          if (metaError) {
            logger.debug('⚠️ Nie udało się zapisać metadanych akceptacji:', metaError)
          }
        }
      } catch (metaCatch) {
        logger.debug('⚠️ Wyjątek przy zapisie metadanych akceptacji:', metaCatch)
      }

      // Nie akceptuj zaproszenia tutaj - zostanie to zrobione na stronie AcceptInvitation po logowaniu
      logger.debug('✅ Rejestracja zakończona, użytkownik zostanie przekierowany do akceptacji zaproszenia')

      // Zaproszenia są teraz automatycznie akceptowane przez trigger bazodanowy po potwierdzeniu email

      // Auto redirect po 2 sekundach do kalendarza
      // Trigger bazodanowy automatycznie zaakceptuje zaproszenie po potwierdzeniu email
      setTimeout(() => {
        navigate('/calendar')
      }, 2000)
    } catch (err: any) {
      setError(err.message || 'Błąd rejestracji')
      captchaRef.current?.reset()
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card-glass p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">
              Konto zostało utworzone!
            </h2>
            <p className="text-slate-600 mb-4">
              Witamy w 2domy.pl! Za chwilę zostaniesz przekierowany do kalendarza.
            </p>
            <div className="animate-pulse text-sm text-slate-500">
              Przekierowywanie...
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo Header */}
        <div className="text-center mb-8">
          <Link to="/" className="relative inline-flex items-center justify-center mb-2" title="Wróć na stronę główną">
            <img src={logo} alt="2domy.pl" className="h-12 w-12 rounded-md shadow-sm" />
          </Link>
          <h1 className="hero-title text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
            <Link to="/" title="Wróć na stronę główną">2domy.pl</Link>
          </h1>
          <p className="text-slate-600 mt-2">
            Stwórz konto i zacznij organizować harmonogram
          </p>
        </div>

        {/* Register Form */}
        <div className="card-glass p-8">
          {/* Sekcja zaproszenia */}
          {invitation && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center mb-2">
                <Users className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-sm font-medium text-blue-800">Zaproszenie do rodziny</h3>
              </div>
              <p className="text-sm text-blue-700">
                Zostałeś zaproszony do dołączenia do rodziny jako{' '}
                <span className="font-medium">
                  {invitation.role === 'parent_a' ? 'Rodzic / Opiekun (Administrator)' :
                    invitation.role === 'parent_b' ? 'Rodzic / Opiekun' :
                      invitation.role === 'child_viewer' ? 'Obserwator' : invitation.role}
                </span>
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Po utworzeniu konta automatycznie dołączysz do rodziny.
              </p>
            </div>
          )}

          {invitationLoading && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                <span className="text-sm text-gray-600">Sprawdzanie zaproszenia...</span>
              </div>
            </div>
          )}

          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-slate-800">
              {invitation ? 'Utwórz konto i dołącz do rodziny' : 'Zarejestruj się'}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {invitation ? 'Wypełnij formularz aby utworzyć konto i dołączyć do rodziny' : 'Wypełnij formularz aby założyć konto'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="twoj@email.com"
                required
                className="w-full bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-20"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Hasło
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 znaków"
                  required
                  className="w-full pr-10 bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Password Requirements */}
              {password && (
                <div className="mt-2 space-y-1">
                  <div className={`text-xs flex items-center gap-1 ${passwordValidation.length ? 'text-green-600' : 'text-slate-500'}`}>
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.length ? 'bg-green-500' : 'bg-slate-300'}`} />
                    Minimum 8 znaków
                  </div>
                  <div className={`text-xs flex items-center gap-1 ${passwordValidation.hasLetter ? 'text-green-600' : 'text-slate-500'}`}>
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.hasLetter ? 'bg-green-500' : 'bg-slate-300'}`} />
                    Zawiera litery
                  </div>
                  <div className={`text-xs flex items-center gap-1 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-slate-500'}`}>
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.hasNumber ? 'bg-green-500' : 'bg-slate-300'}`} />
                    Zawiera cyfry
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1">
                Powtórz hasło
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Powtórz hasło"
                  required
                  className="w-full pr-10 bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-20"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {confirmPassword && (
                <div className={`text-xs mt-1 flex items-center gap-1 ${passwordValidation.match ? 'text-green-600' : 'text-red-500'}`}>
                  <div className={`w-2 h-2 rounded-full ${passwordValidation.match ? 'bg-green-500' : 'bg-red-500'}`} />
                  {passwordValidation.match ? 'Hasła się zgadzają' : 'Hasła się nie zgadzają'}
                </div>
              )}
            </div>

            {/* Terms acceptance */}
            <div className="pt-1">
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  required
                />
                <span>
                  Akceptuję
                  {' '}<a href="/terms" className="text-blue-600 hover:text-blue-700 underline">Regulamin</a>
                  {' '}oraz zapoznałem/am się z
                  {' '}<a href="/polityka-prywatnosci" className="text-blue-600 hover:text-blue-700 underline">Polityką prywatności</a>.
                </span>
              </label>
            </div>

            <CaptchaGate ref={captchaRef} onToken={setCaptchaToken} action="register" />

            <Button
              type="submit"
              disabled={loading || !isPasswordValid || !acceptedTerms}
              className="w-full neon-button"
              onClick={() => {
                try {
                  const w = window as any
                  if (typeof w?.gtagSendEvent === 'function') {
                    // Google Consent Mode v2 automatycznie decyduje czy wysłać
                    w.gtagSendEvent(null)
                  }
                } catch (_) { }
              }}
            >
              {loading ? 'Tworzenie konta...' : 'Stwórz konto'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Masz już konto?{' '}
              <Link
                to="/auth/login"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Zaloguj się
              </Link>
            </p>
          </div>



          {/* Privacy Links */}
          <div className="mt-4 text-center text-xs text-gray-400 space-x-4">
            <button
              className="hover:underline bg-transparent border-0 p-0 text-inherit cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                showPreferences()
              }}
            >
              Preferencje zgody
            </button>
            <span>•</span>
            <a href="/polityka-prywatnosci" className="hover:underline">Polityka prywatności</a>
          </div>
        </div>
      </div>
    </div>
  )
} 